import { Column } from "@ant-design/charts";
import {
	CalendarOutlined,
	CheckCircleOutlined,
	DashboardOutlined,
	DollarOutlined,
	InboxOutlined,
	UserOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Grid,
	Row,
	Space,
	Statistic,
	Table,
	Tag,
} from "antd";
import dayjs from "dayjs"; // Make sure dayjs is imported
import React from "react";
import useBillStore from "../../stores/billStore";
import useBookingStore from "../../stores/bookingStore";
import useUserStore from "../../stores/userStore";
import AdminPageLayout from "./layout/AdminPageLayout";

const { useBreakpoint } = Grid;

const Dashboard = ({ userData }) => {
	const screens = useBreakpoint();
	const [containerWidth, setContainerWidth] = React.useState(0);
	const containerRef = React.useRef(null);
	const { usersCount, fetchUsersCount } = useUserStore();
	const { bookings, loading, fetchBookings } = useBookingStore();
	const { bills, fetchBills } = useBillStore();

	// Add resize observer to track container width
	React.useEffect(() => {
		const observer = new ResizeObserver((entries) => {
			setContainerWidth(entries[0].contentRect.width);
		});

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, []);

	React.useEffect(() => {
		fetchUsersCount();
	}, []);

	// Fetch all bookings and bills
	React.useEffect(() => {
		fetchBookings();
		fetchBills();
	}, []);

	const getRevenueStats = () => {
		// Sum net_price values from bookings that are completed (past/current)
		const currentDate = dayjs();
		return bookings
			.filter((booking) => {
				if (booking.check_in) {
					return dayjs(booking.check_in).isBefore(
						currentDate.add(1, "day"),
						"day",
					);
				}
				return false;
			})
			.reduce((total, booking) => {
				return total + (parseFloat(booking.net_price) || 0);
			}, 0)
			.toFixed(2);
	};

	const getFutureRevenueStats = () => {
		// Sum net_price values from bookings that are in the future
		const currentDate = dayjs();
		return bookings
			.filter((booking) => {
				if (booking.check_in) {
					return dayjs(booking.check_in).isAfter(currentDate, "day");
				}
				return false;
			})
			.reduce((total, booking) => {
				return total + (parseFloat(booking.net_price) || 0);
			}, 0)
			.toFixed(2);
	};

	const getPendingBookings = () => {
		return bookings.filter(
			(booking) =>
				booking.status === "pending" || booking.status === "confirmed",
		).length;
	};

	const getTotalBookings = () => {
		return bookings.length;
	};

	// Calculate total bills
	const getTotalBills = () => {
		return bills
			.reduce((total, bill) => {
				return total + parseFloat(bill.amount || 0);
			}, 0)
			.toFixed(2);
	};

	// Calculate net revenue (revenue - bills) by month
	const getRevenueByMonth = () => {
		const monthlyData = {};
		const currentDate = dayjs();

		bookings.forEach((booking) => {
			if (booking.check_in) {
				const month = dayjs(booking.check_in).format("MMM YYYY");
				const revenue = parseFloat(booking.net_price) || 0;

				if (!monthlyData[month]) {
					monthlyData[month] = { revenue: 0, cost: 0 };
				}
				monthlyData[month].revenue += revenue;
			}
		});

		bills.forEach((bill) => {
			if (bill.due_date) {
				const month = dayjs(bill.due_date).format("MMM YYYY");
				const cost = parseFloat(bill.amount) || 0;

				if (!monthlyData[month]) {
					monthlyData[month] = { revenue: 0, cost: 0 };
				}
				monthlyData[month].cost += cost;
			}
		});

		// Convert to array format with NET calculation, excluding future months
		const chartData = [];
		Object.entries(monthlyData)
			.sort((a, b) => dayjs(a[0], "MMM YYYY").diff(dayjs(b[0], "MMM YYYY")))
			.forEach(([month, data]) => {
				const monthDate = dayjs(month, "MMM YYYY");
				// Only include months that are not in the future
				if (
					monthDate.isBefore(currentDate, "month") ||
					monthDate.isSame(currentDate, "month")
				) {
					const net = data.revenue - data.cost;
					chartData.push({
						month,
						net: parseFloat(net.toFixed(2)),
					});
				}
			});

		return chartData;
	};

	const bookingColumns = [
		{
			title: "Customer",
			dataIndex: "customer_name",
			key: "customer",
			render: (name) => name || "N/A",
		},
		{
			title: "Platform",
			dataIndex: "platform_details",
			key: "platform",
			render: (platform) => platform?.name || "N/A",
		},
		{
			title: "Check-in",
			dataIndex: "check_in",
			key: "check_in",
			render: (text) => (
				<Space>
					<CalendarOutlined />
					{dayjs(text).format("DD-MMM")}
				</Space>
			),
		},
		{
			title: "Check-out",
			dataIndex: "check_out",
			key: "check_out",
			render: (text) => dayjs(text).format("DD-MMM"),
		},
		{
			title: "Price",
			dataIndex: "net_price",
			key: "net_price",
			render: (amount) => {
				// Convert to number first, then apply toFixed
				const numericAmount = parseFloat(amount) || 0;
				return `$${numericAmount.toFixed(2)}`;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				let color = "blue";
				if (status === "completed") color = "green";
				if (status === "Reserved") color = "green";
				if (status === "cancelled") color = "red";

				return (
					<Tag color={color}>
						<CheckCircleOutlined /> {status}
					</Tag>
				);
			},
		},
	];

	const billColumns = [
		{
			title: "Bill Type",
			dataIndex: "bill_type",
			key: "bill_type",
			render: (type) => type || "N/A",
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			render: (amount) => {
				const numericAmount = parseFloat(amount) || 0;
				return `€${numericAmount.toFixed(2)}`;
			},
		},
		{
			title: "Due Date",
			dataIndex: "due_date",
			key: "due_date",
			render: (text) => (
				<Space>
					<CalendarOutlined />
					{dayjs(text).format("DD-MMM-YYYY")}
				</Space>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				const color = status === "paid" ? "green" : "red";

				return (
					<Tag color={color}>
						<WalletOutlined /> {status}
					</Tag>
				);
			},
		},
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			render: (notes) =>
				notes
					? notes.substring(0, 50) + (notes.length > 50 ? "..." : "")
					: "N/A",
		},
	];

	const scrollContainerRef = React.useRef(null);
	const [isDragging, setIsDragging] = React.useState(false);
	const [startX, setStartX] = React.useState(0);
	const [scrollLeft, setScrollLeft] = React.useState(0);

	// Simplify scroll handling - only needed for internal state now
	const handleScroll = () => {
		// Keep this empty or remove if not needed for other functionality
	};

	// Mouse drag handlers for scroll
	const handleMouseDown = (e) => {
		if (scrollContainerRef.current) {
			setIsDragging(true);
			setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
			setScrollLeft(scrollContainerRef.current.scrollLeft);
			// Change the cursor to grabbing
			scrollContainerRef.current.style.cursor = "grabbing";
			scrollContainerRef.current.style.userSelect = "none";
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
		if (scrollContainerRef.current) {
			// Change cursor back to grab
			scrollContainerRef.current.style.cursor = "grab";
			scrollContainerRef.current.style.userSelect = "";
		}
	};

	const handleMouseLeave = () => {
		if (isDragging) {
			setIsDragging(false);
			if (scrollContainerRef.current) {
				scrollContainerRef.current.style.cursor = "grab";
				scrollContainerRef.current.style.userSelect = "";
			}
		}
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;
		e.preventDefault();

		if (scrollContainerRef.current) {
			const x = e.pageX - scrollContainerRef.current.offsetLeft;
			const walk = (x - startX) * 2; // Scroll speed multiplier
			scrollContainerRef.current.scrollLeft = scrollLeft - walk;
		}
	};

	// Add event listeners for document mouse up to handle cases where mouse is released outside the container
	React.useEffect(() => {
		document.addEventListener("mouseup", handleMouseUp);
		return () => {
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);

	return (
		<AdminPageLayout
			title={`Welcome back, ${userData.username}!`}
			icon={<DashboardOutlined />}
			subtitle="Here's your property overview"
			listItems={[
				"Track bookings and revenue",
				"Manage properties",
				"View customer insights",
			]}
		>
			<Space direction="vertical" size="large" style={{ width: "100%" }}>
				<Card
					bodyStyle={{
						padding: screens.md ? "24px" : "12px",
						position: "relative",
					}}
				>
					<div className="stats-container" style={{ position: "relative" }}>
						{/* Remove left and right scroll buttons */}

						<div
							ref={scrollContainerRef}
							onScroll={handleScroll}
							onMouseDown={handleMouseDown}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseLeave}
							onMouseMove={handleMouseMove}
							style={{
								display: "flex",
								overflowX: "auto",
								scrollbarWidth: "none" /* Firefox */,
								msOverflowStyle: "none" /* IE and Edge */,
								padding: "4px 8px 12px 8px" /* Consistent padding */,
								margin: "0 -8px",
								scrollBehavior: isDragging ? "auto" : "smooth",
								position: "relative",
								cursor: "grab" /* Indicate draggable */,
							}}
							className="custom-scroll-container"
						>
							{/* Custom CSS for hiding scrollbar and dragging effects */}
							<style jsx="true">{`
                                .custom-scroll-container::-webkit-scrollbar {
                                    display: none;
                                }
                                .stat-card {
                                    transition: all 0.3s ease;
                                    border-radius: 12px;
                                    overflow: hidden;
                                    pointer-events: ${isDragging ? "none" : "auto"};
                                }
                                .stat-card:hover {
                                    transform: translateY(-5px);
                                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                                }
                                .custom-scroll-container * {
                                    user-select: ${isDragging ? "none" : "auto"};
                                }
                            `}</style>

							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Pending Bookings"
										value={getPendingBookings()}
										prefix={<CalendarOutlined />}
										valueStyle={{ color: "#1890ff" }}
									/>
								</Card>
							</div>
							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Total Bookings"
										value={getTotalBookings()}
										prefix={<InboxOutlined />}
										valueStyle={{ color: "#52c41a" }}
									/>
								</Card>
							</div>
							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Net Revenue"
										value={getRevenueStats()}
										prefix={<DollarOutlined />}
										valueStyle={{ color: "#389e0d" }}
										precision={2}
										suffix="€"
									/>
								</Card>
							</div>
							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Future Revenue"
										value={getFutureRevenueStats()}
										prefix={<DollarOutlined />}
										valueStyle={{ color: "#faad14" }}
										precision={2}
										suffix="€"
									/>
								</Card>
							</div>
							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Total Bills"
										value={getTotalBills()}
										prefix={<WalletOutlined />}
										valueStyle={{ color: "#f5222d" }}
										precision={2}
										suffix="€"
									/>
								</Card>
							</div>
							<div style={{ minWidth: "200px", padding: "0 8px" }}>
								<Card
									bordered={false}
									className="stat-card"
									style={{ height: "100%" }}
								>
									<Statistic
										title="Total Users"
										value={usersCount}
										prefix={<UserOutlined />}
										valueStyle={{ color: "#faad14" }}
									/>
								</Card>
							</div>
						</div>
					</div>
				</Card>

				<Card
					title={
						<Space>
							<CalendarOutlined />
							Recent Bookings
						</Space>
					}
				>
					<Table
						columns={bookingColumns}
						dataSource={bookings.filter((booking) =>
							["completed", "Reserved", "cancelled"].includes(booking.status),
						)}
						pagination={false}
						size="middle"
						loading={loading}
						rowKey="id"
					/>
				</Card>

				<Card
					title={
						<Space>
							<WalletOutlined />
							Recent Bills
						</Space>
					}
				>
					<Table
						columns={billColumns}
						dataSource={bills}
						pagination={false}
						size="middle"
						loading={loading}
						rowKey="id"
					/>
				</Card>

				<Card
					title={
						<Space>
							<DollarOutlined />
							Net Revenue (Revenue - Bills) by Month
						</Space>
					}
				>
					<Column
						data={getRevenueByMonth()}
						xField="month"
						yField="net"
						column={{
							style: {
								radius: [8, 8, 0, 0],
							},
						}}
						color={({ net }) => (net >= 0 ? "#52c41a" : "#f5222d")}
						label={{
							position: "top",
							style: {
								fill: "#000000",
								opacity: 0.7,
								fontSize: 12,
							},
						}}
						xAxis={{
							type: "cat",
							label: {
								autoRotate: false,
							},
						}}
						yAxis={{
							label: {
								autoHide: true,
								autoRotate: false,
							},
						}}
						meta={{
							net: {
								alias: "Net Revenue (€)",
							},
						}}
						tooltip={{
							formatter: (datum) => {
								return {
									name: "Net Revenue",
									value: `€${datum.net.toFixed(2)}`,
								};
							},
						}}
					/>
				</Card>
			</Space>
		</AdminPageLayout>
	);
};

export default Dashboard;
